import React, { useState, useEffect, useCallback } from "react";
import { buildScopedApiUrl, ensureArray, getApiHeaders } from "./config";
import "./TradingSignalHistory.css"; // Ensure CSS is properly styled
import { useWorkspace } from "./workspace/WorkspaceContext";

function TradingSignalHistory() {
  const { workspace } = useWorkspace();
  const [historyData, setHistoryData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframes, setTimeframes] = useState([]);
  const [candleCloseTimes, setCandleCloseTimes] = useState([]);
  
  // **Filters & Search**
  const [selectedTimeframe, setSelectedTimeframe] = useState("");
  const [selectedCandleCloseTime, setSelectedCandleCloseTime] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // ✅ Search Functionality

  const CATEGORIES = {
    Bullish: [
      { key: "emaBullishCrossSymbols", label: "EMA Bullish Crossover" },
      { key: "dcaBullish", label: "DCA Bullish Signals" }, 
      { key: "stochRsiBullishCrossSymbols", label: "Stoch RSI Bullish Crossover" },
      { key: "rsiBullishReversalSymbols", label: "RSI Bullish Reversal" },
      { key: "bothStochRsiAndRsiBullishSymbols", label: "Both Stoch RSI & RSI Bullish" },
      { key: "rsiOversoldSymbols", label: "RSI Oversold" },
      { key: "stochRsiOverSoldSymbols", label: "Stoch RSI Oversold" }
    ],
    Bearish: [
      { key: "emaBearishCrossSymbols", label: "EMA Bearish Crossover" },
      { key: "dcaBearish", label: "DCA Bearish Signals" },
      { key: "stochRsiBearishCrossSymbols", label: "Stoch RSI Bearish Crossover" },
      { key: "rsiBearishReversalSymbols", label: "RSI Bearish Reversal" },
      { key: "bothStochRsiAndRsiBearishSymbols", label: "Both Stoch RSI & RSI Bearish" },
      { key: "rsiOverboughtSymbols", label: "RSI Overbought" },
      { key: "stochRsiOverBoughtSymbols", label: "Stoch RSI Overbought" }
    ]
  };

   // ✅ Wrap filterData in useCallback to prevent unnecessary re-renders
   const filterData = useCallback((data) => {
    const safeData = ensureArray(data);
    if (safeData.length === 0) {
      setFilteredData([]);
      setTimeframes([]);
      setCandleCloseTimes([]);
      return;
    }

    let filtered = [...safeData];

    if (selectedTimeframe) {
      filtered = filtered.filter(item => item.timeFrame === parseInt(selectedTimeframe));
    }

    if (selectedCandleCloseTime) {
      filtered = filtered.filter(item => item.calculatedCandleCloseTime === selectedCandleCloseTime);
    }

    // ✅ Extract Symbols Correctly from `signalFlyTradingSignals`
    filtered = filtered.map(signal => {
      if (!signal.signalFlyTradingSignals) return signal;

      const signalData = signal.signalFlyTradingSignals || {};

      return {
        ...signal,
        ...signalData,
        dcaBullish: [...new Set((signalData.dcaSymbols || []).filter(sym => sym.includes("LONG")))].sort(),
        dcaBearish: [...new Set((signalData.dcaSymbols || []).filter(sym => sym.includes("SHORT")))].sort(),
      };
    });

    // ✅ Apply Search Filtering
    if (searchQuery) {
      const regex = new RegExp(searchQuery, "i");
      filtered = filtered.map(signal => {
        const filteredCategories = {};
        Object.entries(signal).forEach(([key, symbols]) => {
          if (Array.isArray(symbols)) {
            filteredCategories[key] = symbols.filter(symbol => regex.test(symbol));
          }
        });
        return { ...signal, ...filteredCategories };
      });
    }

    setFilteredData(filtered);

    // ✅ Extract Unique Filters
    setTimeframes([...new Set(filtered.map(item => item.timeFrame))].sort((a, b) => a - b));
    setCandleCloseTimes([...new Set(filtered.map(item => item.calculatedCandleCloseTime))].sort());
  }, [selectedTimeframe, selectedCandleCloseTime, searchQuery]);

  // Ensure fetchData is wrapped in useCallback to avoid re-renders
const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        buildScopedApiUrl('/api/bot/ui/get/getLastTenSignalFlyTradingSignalsHistory', workspace),
        {
          headers: getApiHeaders()
        }
      );
      if (!response.ok) throw new Error("Failed to fetch data");
  
      const text = await response.text();
      if (text) {
        const rawData = JSON.parse(text);
        const safeData = ensureArray(rawData);
        if (!Array.isArray(rawData)) {
          console.warn("Unexpected trading signal history payload shape", workspace, rawData);
        }
        setHistoryData(safeData);
        setError(null);
      } else {
        setHistoryData([]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [workspace]);
  
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]); // ✅ Now correctly dependent on fetchData
  
  useEffect(() => {
    filterData(historyData);
  }, [historyData, selectedTimeframe, selectedCandleCloseTime, searchQuery, filterData]);
  
 
  
  if (loading) {
    return (
      <div className="loading-container">
        ⏳ Loading Recent Trading Signal History...
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        ❌ Failed to fetch data. Please check your connection or try again later.
      </div>
    );
  }
  
  const openTradingViewChart = (symbol) => {
    const cleanedSymbol = symbol.replace(/\s*(LONG|SHORT)$/, "");
    const suffix = workspace.marketType === 'PERPS' ? '.P' : '';
    const tradingViewUrl = `https://www.tradingview.com/chart/?symbol=${workspace.exchange}:${cleanedSymbol}${suffix}`;
    window.open(tradingViewUrl, "_blank");
  };

  return (
    <div className="trading-dashboard">
      <h2 className="dashboard-title">📜 Trading Signal History</h2>

      <div className="filters">
        <select onChange={(e) => setSelectedTimeframe(e.target.value)} value={selectedTimeframe} className="dropdown">
          <option value="">🕒 All Timeframes</option>
          {timeframes.map((tf) => (
            <option key={tf} value={tf}>{tf} min</option>
          ))}
        </select>

        <select onChange={(e) => setSelectedCandleCloseTime(e.target.value)} value={selectedCandleCloseTime} className="dropdown">
          <option value="">📅 All Candle Close Times</option>
          {candleCloseTimes.map((ct) => (
            <option key={ct} value={ct}>{ct}</option>
          ))}
        </select>

        <input 
          type="text" 
          placeholder="🔍 Search Symbol..." 
          value={searchQuery} 
          onChange={(e) => setSearchQuery(e.target.value.toUpperCase())} 
          className="search-box" 
        />
      </div>

      <div className="categories">
        {Object.entries(CATEGORIES).map(([category, subcategories]) => (
          <div key={category} className={`category ${category.toLowerCase()}`}>
            <h3 className={`category-title ${category.toLowerCase()}-title`}>
              {category === "Bullish" ? "📈 Bullish Signals" : "📉 Bearish Signals"}
            </h3>
            {subcategories.map(({ key, label }) => (
              <div key={key} className="category-section">
                <h4 className="subcategory-title">{label}</h4>
                <ul className="symbol-list">
                {filteredData.length > 0 ? (
                    Array.from(new Set(ensureArray(filteredData).flatMap(signal => ensureArray(signal?.[key]))))
                    .sort()
                    .map((symbol, idx) => (
                    <li key={idx} className="symbol-item">
                        {symbol.replace(/\s*(LONG|SHORT)$/, "")}
                        <span className="chart-icon" onClick={() => openTradingViewChart(symbol)}>📈</span>
                    </li>
                    ))
                ) : (
                    <li className="no-data">No Data Available</li>
                )}
                </ul>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default TradingSignalHistory;

